import { Injectable, signal } from '@angular/core';
import { Subject } from 'rxjs';
import { TelnyxRTC, TelnyxCall } from '@telnyx/webrtc';
import { TELNYX_CONSTANTS } from './telnyx.constants';

export interface AudioDevices {
  microphones: MediaDeviceInfo[];
  speakers: MediaDeviceInfo[];
}

export interface AudioEvent {
  event: 'stream_attached' | 'playback_started' | 'autoplay_blocked' | 'routing_failed';
  data?: any;
}

/**
 * TelnyxAudioService - Handles all audio-related functionality
 *
 * Responsibilities:
 * - Audio device enumeration and selection
 * - Remote audio stream routing to speakers
 * - Handling browser autoplay policies
 * - Audio element lifecycle management
 */
@Injectable({
  providedIn: 'root'
})
export class TelnyxAudioService {
  private remoteAudioElement: HTMLAudioElement | null = null;
  private streamPollInterval: ReturnType<typeof setInterval> | null = null;
  private streamPollTimeout: ReturnType<typeof setTimeout> | null = null;

  private audioEventsSubject = new Subject<AudioEvent>();
  public audioEvents$ = this.audioEventsSubject.asObservable();

  /**
   * Get available audio input/output devices
   */
  async getAudioDevices(): Promise<AudioDevices> {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return {
      microphones: devices.filter(d => d.kind === 'audioinput'),
      speakers: devices.filter(d => d.kind === 'audiooutput'),
    };
  }

  /**
   * Set audio devices on the Telnyx client
   */
  setAudioDevices(
    client: TelnyxRTC | null,
    microphoneId?: string,
    speakerId?: string
  ): void {
    if (!client) return;

    if (microphoneId && typeof client.setMicrophoneDevice === 'function') {
      client.setMicrophoneDevice(microphoneId);
    }

    if (speakerId) {
      if (typeof client.setSpeakerDevice === 'function') {
        client.setSpeakerDevice(speakerId);
      }
      this.setAudioOutputDevice(speakerId);
    }
  }

  /**
   * Set speaker device for the remote audio element
   */
  private async setAudioOutputDevice(speakerId: string): Promise<void> {
    if (!this.remoteAudioElement || !speakerId) return;

    if ('setSinkId' in this.remoteAudioElement) {
      try {
        await (this.remoteAudioElement as any).setSinkId(speakerId);
      } catch {
        // setSinkId not supported or permission denied - ignore
      }
    }
  }

  /**
   * Start polling for remote stream availability
   * This is more reliable than relying on WebRTC events alone
   */
  startStreamPolling(call: TelnyxCall, onStreamReceived: () => void): void {
    this.stopStreamPolling();

    this.streamPollInterval = setInterval(() => {
      // Stop if call ended
      if (!call || call.state === 'hangup') {
        this.stopStreamPolling();
        return;
      }

      // Stop if already attached
      if (this.remoteAudioElement?.srcObject) {
        this.stopStreamPolling();
        return;
      }

      // Check for remote stream
      if (call.remoteStream) {
        this.stopStreamPolling();
        this.attachRemoteStream(call.remoteStream);
        onStreamReceived();
        return;
      }

      // Fallback: check peer connection directly
      const pc = (call as any).peer;
      if (pc?.getRemoteStreams) {
        const streams = pc.getRemoteStreams();
        const audioStream = streams?.find((s: MediaStream) => s.getAudioTracks().length > 0);
        if (audioStream) {
          this.stopStreamPolling();
          (call as any).remoteStream = audioStream;
          this.attachRemoteStream(audioStream);
          onStreamReceived();
        }
      }
    }, TELNYX_CONSTANTS.AUDIO.POLL_INTERVAL_MS);

    // Stop polling after timeout
    this.streamPollTimeout = setTimeout(() => {
      this.stopStreamPolling();
    }, TELNYX_CONSTANTS.AUDIO.POLL_TIMEOUT_MS);
  }

  /**
   * Stop stream polling
   */
  stopStreamPolling(): void {
    if (this.streamPollInterval) {
      clearInterval(this.streamPollInterval);
      this.streamPollInterval = null;
    }
    if (this.streamPollTimeout) {
      clearTimeout(this.streamPollTimeout);
      this.streamPollTimeout = null;
    }
  }

  /**
   * Attach remote stream to audio element and start playback
   */
  async attachRemoteStream(stream: MediaStream): Promise<boolean> {
    // Create audio element if needed
    if (!this.remoteAudioElement) {
      this.remoteAudioElement = document.createElement('audio');
      this.remoteAudioElement.autoplay = true;
      (this.remoteAudioElement as any).playsInline = true;
      this.remoteAudioElement.style.display = 'none';
      document.body.appendChild(this.remoteAudioElement);
    }

    // Attach stream
    this.remoteAudioElement.srcObject = stream;
    this.audioEventsSubject.next({ event: 'stream_attached' });

    // Try to play
    try {
      await this.remoteAudioElement.play();
      this.audioEventsSubject.next({ event: 'playback_started' });
      return true;
    } catch (error) {
      // Autoplay blocked - user interaction needed
      this.audioEventsSubject.next({
        event: 'autoplay_blocked',
        data: { reason: 'User interaction required' }
      });

      // Retry after short delay
      setTimeout(() => this.triggerPlayback(), 1000);
      return false;
    }
  }

  /**
   * Manually trigger audio playback (call after user interaction)
   */
  async triggerPlayback(): Promise<boolean> {
    if (!this.remoteAudioElement || !this.remoteAudioElement.paused) {
      return true;
    }

    try {
      await this.remoteAudioElement.play();
      this.audioEventsSubject.next({ event: 'playback_started' });
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Test audio output by playing a short tone
   */
  async testAudioOutput(): Promise<boolean> {
    try {
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      const ctx = new AudioContextClass();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 440;
      gain.gain.value = 0.1;

      osc.start();
      osc.stop(ctx.currentTime + 0.2);

      return true;
    } catch {
      return false;
    }
  }

  /**
   * Apply default speaker to audio element
   */
  async applyDefaultSpeaker(): Promise<void> {
    if (!this.remoteAudioElement) return;

    try {
      const devices = await this.getAudioDevices();
      if (devices.speakers.length > 0) {
        await this.setAudioOutputDevice(devices.speakers[0].deviceId);
      }
    } catch {
      // Ignore - device selection is optional
    }
  }

  /**
   * Check if audio is currently playing
   */
  isPlaying(): boolean {
    return !!(this.remoteAudioElement && !this.remoteAudioElement.paused);
  }

  /**
   * Clean up audio element
   */
  cleanup(): void {
    this.stopStreamPolling();

    if (this.remoteAudioElement) {
      this.remoteAudioElement.pause();
      this.remoteAudioElement.srcObject = null;
      if (this.remoteAudioElement.parentNode) {
        this.remoteAudioElement.parentNode.removeChild(this.remoteAudioElement);
      }
      this.remoteAudioElement = null;
    }
  }
}
